import { usePrefersReducedMotion, useToken } from '@chakra-ui/react';
import type { ContentTranslationUiStatus } from '@duke-hq/shared-constants/contentTranslationStatus';
import { DOT_TINT_TRANSITION, TINT_TRANSITION } from './translationsMotion';

// Every status sits on one continuous colour ramp, so a change interpolates
// *along* it (blue -> green through teal, reading as colour flowing forward)
// rather than swapping a `colorScheme` class, which CSS can't animate. Concrete
// theme values are resolved so the chip transitions real bg/color/border.
//
// `breathes` is in-flight work only; a steady error/stale dot sits quietly.
// `available-partial` stays on the green ramp (the content is served); its dot
// renders as a half-filled ring, so it needs the full-strength shade to read.
const STATUS_RAMP: Record<
  ContentTranslationUiStatus,
  { scheme: string; dotShade: string; breathes: boolean }
> = {
  'not-translated': { scheme: 'gray', dotShade: '400', breathes: false },
  queued: { scheme: 'blue', dotShade: '500', breathes: true },
  generating: { scheme: 'blue', dotShade: '500', breathes: true },
  available: { scheme: 'green', dotShade: '500', breathes: false },
  'available-partial': { scheme: 'green', dotShade: '500', breathes: false },
  stale: { scheme: 'orange', dotShade: '500', breathes: false },
  failed: { scheme: 'red', dotShade: '500', breathes: false },
  // Deliberately stopped: the grey family like not-translated (the cell is
  // simply untranslated again), told apart by the darker dot and its own label.
  cancelled: { scheme: 'gray', dotShade: '500', breathes: false },
};

// The fully-arrived state that earns the completion settle (the dot pop).
// `available-partial` is excluded on purpose: some content is still English, so
// landing partial should not read as a completed arrival.
export function isArrived(status: ContentTranslationUiStatus): boolean {
  return status === 'available';
}

export type StatusTransition = {
  bg: string;
  color: string;
  borderColor: string;
  dot: string;
  chipTransition: string;
  dotTransition: string;
  shouldBreathe: boolean;
  // When the user prefers reduced motion, callers degrade to the end-state.
  motionDisabled: boolean;
};

// Resolves the colour-ramp tokens and shared motion strings for a status, gated
// once on `prefers-reduced-motion`, so the chip, dot, and matrix read and animate
// alike. When motion is off the colours are unchanged but the transitions collapse
// to `none`, landing instantly with no teal frame and no breath.
export function useStatusTransition(status: ContentTranslationUiStatus): StatusTransition {
  const motionDisabled = usePrefersReducedMotion();
  const { scheme, dotShade, breathes } = STATUS_RAMP[status];

  // Every shade we might land on, in a stable order, so the hook call is unconditional.
  const [bg, color, borderColor, dot] = useToken('colors', [
    `${scheme}.50`,
    `${scheme}.700`,
    `${scheme}.200`,
    `${scheme}.${dotShade}`,
  ]);

  return {
    bg,
    color,
    borderColor,
    dot,
    chipTransition: motionDisabled ? 'none' : TINT_TRANSITION,
    dotTransition: motionDisabled ? 'none' : DOT_TINT_TRANSITION,
    shouldBreathe: !motionDisabled && breathes,
    motionDisabled,
  };
}
